import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { SystemHealth } from '../../types/titan';

const HealthTab: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [dataSources, setDataSources] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const [hRes, dsRes] = await Promise.all([
        adminApi.systemHealth(),
        adminApi.dataSources(),
      ]);
      setHealth(hRes);
      setDataSources(dsRes);
    } catch (e) {
      console.error('Health fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const statusColor = (s: string) => {
    if (!s) return 'bg-gray-500/10 text-gray-500';
    const sl = s.toLowerCase();
    if (sl === 'healthy' || sl === 'ok' || sl === 'running' || sl === 'connected' || sl === 'fresh') return 'bg-green-500/10 text-green-600';
    if (sl === 'warning' || sl === 'degraded' || sl === 'stale') return 'bg-yellow-500/10 text-yellow-600';
    return 'bg-red-500/10 text-red-600';
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Live platform health</p>
        <Button variant="outline" size="sm" onClick={fetchHealth}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {health && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="p-3 text-center">
              <p className="text-[11px] text-muted-foreground">Status</p>
              <Badge className={`mt-1 ${statusColor(health.status)}`}>{health.status || 'Unknown'}</Badge>
            </Card>
            {(health as any).controller && (
              <>
                <Card className="p-3 text-center">
                  <p className="text-[11px] text-muted-foreground">Controller</p>
                  <Badge className={`mt-1 ${statusColor(((health as any).controller.scanner_online ?? (health as any).controller.is_running) ? 'running' : 'stopped')}`}>
                    {((health as any).controller.scanner_online ?? (health as any).controller.is_running) ? 'Running' : 'Stopped'}
                  </Badge>
                </Card>
                <Card className="p-3 text-center">
                  <p className="text-[11px] text-muted-foreground">Positions</p>
                  <p className="text-lg font-bold mt-0.5">{(health as any).controller.open_positions ?? 0}</p>
                </Card>
                <Card className="p-3 text-center">
                  <p className="text-[11px] text-muted-foreground">Circuit Breaker</p>
                  <Badge className={`mt-1 ${(health as any).controller.circuit_breaker_halted ? 'bg-red-500/10 text-red-600' : 'bg-green-500/10 text-green-600'}`}>
                    {(health as any).controller.circuit_breaker_halted ? 'HALTED' : 'OK'}
                  </Badge>
                </Card>
              </>
            )}
          </div>

          {health.components && (
            <Card className="p-4">
              <h3 className="text-sm font-semibold mb-2">Component Status</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(health.components).map(([name, info]) => (
                  <div key={name} className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/30">
                    <span className="text-xs font-medium capitalize">{name}</span>
                    <div className="flex items-center gap-2">
                      {info.latency_ms != null && <span className="text-[10px] text-muted-foreground">{info.latency_ms}ms</span>}
                      <Badge variant="outline" className={`text-[10px] ${statusColor(info.status)}`}>
                        {info.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {dataSources && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-2">Data Sources</h3>
          <div className="space-y-1.5">
            {(Array.isArray(dataSources) ? dataSources : dataSources.sources || []).map((src: any, i: number) => (
              <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/30">
                <span className="text-xs font-medium">{src.name || src.source || `Source ${i}`}</span>
                <div className="flex items-center gap-2">
                  {src.asset_class && <span className="text-[10px] text-muted-foreground">{src.asset_class}</span>}
                  <Badge variant="outline" className={`text-[10px] ${statusColor(src.status || src.health || '')}`}>
                    {src.status || src.health || 'unknown'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!health && !dataSources && (
        <div className="text-center py-12 text-muted-foreground">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Health data unavailable</p>
          <p className="text-xs mt-1">Backend may not be running</p>
        </div>
      )}
    </div>
  );
};

export default HealthTab;
